import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { SideMenuContextProvider } from "contexts/SideMenuContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PressLogos from "modules/common/components/PressLogos";
import { LANGUAGES_TO_DISPLAY_LUFTHANSA_TOP_BAR, SLUGS_TO_DISPLAY_LUFTHANSA_TOP_BAR } from "modules/home-page/utils";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useTopMenu } from "utils/menuUtils";
import { SideMenu, SideMenuMobileAdapter } from "../../../components/index";
import StickyBottomBarAdapter from "../adapters/StickyBottomBar";

const { Box, GridItem, Heading: ChakraHeading, Text } = Chakra;

const SideBarTemplate = ({ cmsLanguagesData, story, serverSideStoryblokPages, whiteListDomains }) => {
  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      pageTitle,
      pageSubTitle,
      body,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;
  const noindex = tagList.includes("noindex");
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);

  const {
    query: { secondUrlSegment },
  } = useRouter();

  let topBannerText;
  let topBannerButtonText;
  if (
    LANGUAGES_TO_DISPLAY_LUFTHANSA_TOP_BAR.includes(language) &&
    SLUGS_TO_DISPLAY_LUFTHANSA_TOP_BAR.includes(secondUrlSegment)
  ) {
    topBannerText = translations("top_bar_affected_by_lufthansa_strike");
    topBannerButtonText = translations("top_bar_submit_claim_now");
  }
  const marginTop = topBannerText && topBannerButtonText ? { base: 20, md: 10 } : 0;

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={metaTitle} description={metaDescription} noindex={noindex} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <SideMenuContextProvider>
            <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
            <HeaderNav
              menuItems={<MenuItems menu={topMenu} />}
              headerButtons={<CheckCompensationButton />}
              topBannerText={topBannerText}
              topBannerButtonText={topBannerButtonText}
              subMenu={<SideMenuMobileAdapter pageHeading={pageTitle} />}
            />
            <Box mt={marginTop} />
            <Grid mb={{ base: 20, md: 32 }} pt={{ base: 20, md: 32 }}>
              <GridItem gridColumn={{ base: "initial", md: "span 3" }} as="aside">
                <Box position="sticky" overflow="scroll" maxH="calc(100vh - 170px)" top={128} display={{ base: "none", md: "block" }} >
                  <SideMenu pageHeading={pageTitle} />
                </Box>
              </GridItem>
              <GridItem gridColumn={{ base: "initial", md: "span 8" }}>
                <ChakraHeading variant="h1" as="h1">
                  {pageTitle}
                </ChakraHeading>
                <Text mt={4}>{pageSubTitle}</Text>
                {body
                  ? body.map((blok) => (
                      <Box key={blok._uid} _notLast={{ mb: 10 }}>
                        <DynamicComponent blok={blok} />
                      </Box>
                    ))
                  : null}
              </GridItem>
            </Grid>
          </SideMenuContextProvider>
          <PressLogos />

          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const SideBarTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <SideBarTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default SideBarTemplateAdapter;

SideBarTemplate.propTypes = {
  cmsLanguagesData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  story: PropTypes.shape({
    tag_list: PropTypes.arrayOf(PropTypes.string),
    full_slug: PropTypes.string,
    content: PropTypes.shape({
      body: PropTypes.arrayOf(PropTypes.shape({})),
      pageTitle: PropTypes.string,
      pageSubTitle: PropTypes.string,
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.string,
      breadcrumbsParentPage: PropTypes.shape({}),
      breadcrumbsPositionName: PropTypes.string,
    }),
  }).isRequired,
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

SideBarTemplate.defaultProps = {
  whiteListDomains: [],
};
